// A linha de filtros do Pipeline (vista · status · vendedor · cliente · origem
// · busca). Os chips de etapa que moravam aqui viraram o funil desenhado
// (FunilEtapas.tsx, 01/10/2026).
//
// Extraída de ComercialPipeline.tsx em 30/09/2026, junto do pedido do Lucas de
// filtrar por cliente: a página é um "entry fino" por decisão de 2026-05-23 (o
// kanban e os formulários já moram aqui em `./comercial-pipeline/`), e separar a
// barra permite montá-la num harness com os dados REAIS de produção — o jest do
// CRA5 não carrega o antd v6, então a única forma de conferir o dropdown de 186
// clientes é no navegador.
//
// A régua de quem passa pelo filtro NÃO está aqui: mora em `filtroCliente.ts`
// (pura, testada no CI) e no `passaFiltros` da página, que é um só para as três
// vistas e para a contagem dos chips.
import { Button, Input, Select, Typography, Radio, Flex } from 'antd';
import {
  PlusOutlined, SearchOutlined, AppstoreOutlined, UnorderedListOutlined, InboxOutlined,
} from '@ant-design/icons';
import { STATUS_LABELS } from './utils';
import { buscaCasaCliente, type OpcaoCliente } from './filtroCliente';

const { Text } = Typography;

export type Vista = 'lista' | 'kanban' | 'calendario';

export function FiltrosPipeline({
  vista, setVista,
  filtroStatus, setFiltroStatus,
  filtroResponsavel, setFiltroResponsavel,
  filtroCliente, setFiltroCliente,
  busca, setBusca,
  responsaveis, opcoesCliente,
  qtdFiltrada, qtdTotal, temFiltro, onLimpar,
  onNovoBriefing, onNovoNegocio,
}: {
  vista: Vista;
  setVista: (v: Vista) => void;
  filtroStatus: string;
  setFiltroStatus: (v: string) => void;
  filtroResponsavel: string;
  setFiltroResponsavel: (v: string) => void;
  filtroCliente: string;
  setFiltroCliente: (v: string) => void;
  busca: string;
  setBusca: (v: string) => void;
  responsaveis: string[];
  opcoesCliente: OpcaoCliente[];
  qtdFiltrada: number;
  qtdTotal: number;
  temFiltro: boolean;
  onLimpar: () => void;
  onNovoBriefing: () => void;
  onNovoNegocio: () => void;
}) {
  return (
    <>
      <Flex gap={10} align="center" wrap="wrap" style={{ marginBottom: 10 }}>
        <Radio.Group value={vista} onChange={(e) => setVista(e.target.value)} size="small">
          <Radio.Button value="lista"><UnorderedListOutlined /> Lista</Radio.Button>
          <Radio.Button value="kanban"><AppstoreOutlined /> Kanban</Radio.Button>
          <Radio.Button value="calendario">📅 Calendário</Radio.Button>
        </Radio.Group>
        <Select value={filtroStatus || undefined} onChange={(v) => setFiltroStatus(v || '')}
          placeholder="Todos status" allowClear showSearch optionFilterProp="label"
          size="small" style={{ width: 160 }}
          options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ label: v, value: k }))} />
        <Select value={filtroResponsavel || undefined} onChange={(v) => setFiltroResponsavel(v || '')}
          placeholder="Todos vendedores" allowClear showSearch optionFilterProp="label"
          size="small" style={{ width: 160 }}
          options={responsaveis.map(r => ({ label: r, value: r }))} />
        {/* Regra da casa: todo dropdown filtra pelo que se escreve. Aqui a busca
            casa razão social, nome fantasia e CNPJ por dígitos — por isso o
            `filterOption` próprio em vez do `optionFilterProp` do antd, que só
            olharia o rótulo. */}
        <Select value={filtroCliente || undefined} onChange={(v) => setFiltroCliente(v || '')}
          placeholder={opcoesCliente.length ? `Todos clientes (${opcoesCliente.length})` : 'Todos clientes'}
          allowClear showSearch size="small" style={{ width: 200 }}
          // O dropdown é mais largo que o campo fechado: o rótulo tem nome +
          // fantasia + contagem, e 200px cortaria a contagem justamente de quem
          // tem nome longo.
          popupMatchSelectWidth={false}
          styles={{ popup: { root: { minWidth: 380, maxWidth: 'min(92vw, 640px)' } } }}
          filterOption={(input, opt) => buscaCasaCliente(input, opt as any)}
          // Nome e contagem em colunas: o nome encolhe com "…" (e o nome
          // completo fica no title), a contagem NUNCA some. Medido em 30/09:
          // 8 dos 187 nomes passam da largura do dropdown, e o "…" comia
          // justamente o numero de negocios, que e o que se quer ver.
          optionRender={(opt) => {
            const o = opt.data as unknown as OpcaoCliente;
            return (
              <span title={o.title} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.nome}
                </span>
                <span style={{ color: '#8A8F9C', fontVariantNumeric: 'tabular-nums' }}>{o.negocios}</span>
              </span>
            );
          }}
          options={opcoesCliente} />
        <Input placeholder="Buscar título, cliente, área, vendedor..." value={busca}
          onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small" allowClear
          style={{ flex: 1, minWidth: 150, maxWidth: 360 }} />
        {temFiltro && (
          <>
            <Text type="secondary" style={{ fontSize: 11 }}>{qtdFiltrada}/{qtdTotal}</Text>
            <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
              onClick={onLimpar}>Limpar filtros</Button>
          </>
        )}
        <Button size="small" type="primary" ghost icon={<InboxOutlined />} style={{ marginLeft: 'auto' }}
          onClick={onNovoBriefing}
          title="Abre o Dwight pré-preparado pra absorver briefing (texto + PDF + KMZ)">
          Novo briefing
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={onNovoNegocio}>
          Novo Negócio
        </Button>
      </Flex>

    </>
  );
}
